import React, { useState, useEffect, useCallback } from 'react';
import EggDetail from 'components/Egg/EggDetail';
import './EggDashboard.scss';
import { useApplyUserPointsToEgg, useFavoriteEgg } from 'utils/PointsAPI';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { Egg, getPassivePoints, useEggContext } from 'contexts/EggContext';
import { EmptyCollection } from 'components/Account/EmptyCollection';
import egg1 from '../../assets/img/egg/egg1.jpg';
import egg2 from '../../assets/img/egg/egg2.jpg';
import egg3 from '../../assets/img/egg/egg3.jpg';
import egg4 from '../../assets/img/egg/egg4.jpg';
import egg5 from '../../assets/img/egg/egg5.jpg';
import { Button } from 'components/Button/Button';
import { Link } from 'react-router-dom';
import { useLoginRedirect } from 'hooks/useLoginRedirect';
import { mutateQuery, queryClient, queryKeys } from '../../contexts/QueryClient';
import { useUser } from '../../hooks/useUser';
import { routes } from '../../routing/routes';
import partition from 'lodash/partition';
import { AutoPairModal } from './AutoPairModal';
import { PassiveOmnigrowTooltip } from './OmnigrowTooltip';
import { useUserPoints } from 'contexts/UserPointsContext';
import Spinner from 'components/Spinner';
import { isEggMaxLevel } from './eggUtils';
import { Egg as EggTab } from './Egg';
import { SelectableEggCardDashboard } from 'components/SelectableCard/Variants/SelectableCardEgg';

type EggGridProps = {
  eggs: Egg[];
  setSelectedEggId: (egg: number) => void;
  selectedEggId?: number;
};

const EggGrid = ({ eggs, setSelectedEggId, selectedEggId }: EggGridProps) => {
  return (
    <div className="egg-grid mt-3">
      {eggs.map((egg) => (
        <SelectableEggCardDashboard
          card={SelectableEggCardDashboard.transformer(egg)}
          key={egg.id}
          isSelected={egg.id === selectedEggId}
          onClick={() => {
            setSelectedEggId(egg.id);
          }}
        />
      ))}
    </div>
  );
};

const eggPlaceholder = [egg1, egg2, egg3, egg4, egg5];

export function EggDashboard() {
  const { account } = useUser();
  const { data, ethAddress } = useEggContext();
  const eggs = data?.eggs;
  const { data: userPoints } = useUserPoints();
  const points = userPoints?.points;
  const [selectedEggId, setSelectedEggId] = useState<number | undefined>();
  const [isAutoPairModalOpen, setIsAutoPairModalOpen] = useState(false);

  const [favoriteEggs, nonFavoriteEggs] = partition(eggs, (egg) => egg.favorite);
  const accountId = account.dusktech?.userId;

  useLoginRedirect({ follow: routes.dashboard() });

  const { mutate: favoriteEgg } = useFavoriteEgg();
  const { mutate: addPoints, isLoading } = useApplyUserPointsToEgg();

  const updateEggs = useCallback(
    (f: (egg: Egg) => Egg) => {
      mutateQuery<Egg[]>(queryKeys.eggs(ethAddress), (currentEggs) => {
        return currentEggs?.map(f) ?? [];
      });
    },
    [ethAddress],
  );

  const setFavoriteEgg = async (gribbleTokenId: number, favoriteBefore: boolean) => {
    if (!accountId) {
      return;
    }
    favoriteEgg(
      {
        userId: accountId,
        favoriteBefore,
        gribbleTokenId,
      },
      {
        onSettled() {
          updateEggs((egg) => {
            if (egg.id === selectedEggId) {
              return {
                ...egg,
                favorite: !egg.favorite,
              };
            } else {
              return egg;
            }
          });
        },
      },
    );
  };

  const applyEggPoints = async (pointsToChange: number) => {
    addPoints(
      {
        userId: account.dusktech?.userId || '',
        pointsChanged: pointsToChange,
      },
      {
        async onSettled(data) {
          if (data) {
            data.eggsPoints.forEach((eggPoint) => {
              updateEggs((egg) => {
                if (egg.id === eggPoint.gribbleTokenId) {
                  return {
                    ...egg,
                    points: eggPoint.points,
                  };
                } else {
                  return egg;
                }
              });
            });
          }
          // we're invalidating the user points instead of using the points returned from
          // the http request to make sure we're properly in-sync
          await queryClient.invalidateQueries(queryKeys.userPoints(accountId));
        },
      },
    );
  };

  useEffect(() => {
    if (!selectedEggId && favoriteEggs && favoriteEggs.length > 0) {
      setSelectedEggId(favoriteEggs[0].id);
    } else if (!selectedEggId && eggs && eggs.length > 0) {
      setSelectedEggId(eggs[0].id);
    }
  }, [selectedEggId, eggs, eggs?.length, favoriteEggs]);

  const selectedEgg = eggs?.find((egg: Egg) => egg.id === selectedEggId);

  if (!eggs || eggs.length === 0) {
    return (
      <EggTab>
        <div className="empty-list">
          <EmptyCollection
            title="Egg Mint is live right now!"
            message="You don’t own any Eggs yet or your transaction is still processing."
            placeholderImages={eggPlaceholder}
          >
            <Link className="nav-link text-center" to={routes.mint()}>
              <Button primary>Mint your egg now</Button>
            </Link>
          </EmptyCollection>
        </div>
      </EggTab>
    );
  }

  return (
    <EggTab>
      <div className="egg-dashboard">
        <div id="egg-list">
          <HorizontalDivider className="d-md-none mb-4" />
          <div className="tab-title pb-3">
            <h1 className="m-0 mb-2 egg-dashboard-title">Your gribble egg collection</h1>
          </div>
          <div className="mb-3 egg-auto-button-wrapper">
            <button
              className="btn btn-secondary btn-sm me-2 egg-auto-button"
              onClick={() => {
                setIsAutoPairModalOpen(true);
              }}
              disabled={account.breakers.length === 0}
            >
              Auto Pair
            </button>

            <OmnigrowSection
              points={points}
              isLoading={isLoading}
              onClick={(points) => applyEggPoints(points)}
              eggs={eggs}
            />
          </div>
          {favoriteEggs.length > 0 && (
            <>
              <h5 className="mb-0">Favorites ({favoriteEggs.length})</h5>
              <EggGrid
                eggs={favoriteEggs}
                setSelectedEggId={setSelectedEggId}
                selectedEggId={selectedEggId}
              />
              <h5 className="mb-0">Other ({nonFavoriteEggs.length})</h5>
            </>
          )}
          <EggGrid
            eggs={nonFavoriteEggs}
            selectedEggId={selectedEggId}
            setSelectedEggId={setSelectedEggId}
          />
        </div>

        {selectedEgg && (
          <div id="egg-detail" className="tab-right">
            <div className="egg-detail-sticky">
              <div className="egg-detail-title">
                <h1 className="egg-dashboard-title">egg #{selectedEgg.id}</h1>
                <div className="egg-passive-omnigrow">
                  <p>
                    <b>+{getPassivePoints(selectedEgg.paired)}</b> OmniGrow/Day
                  </p>
                  <PassiveOmnigrowTooltip paired={selectedEgg.paired} />
                </div>
              </div>
              <EggDetail {...selectedEgg} setFavoriteEgg={setFavoriteEgg} />
            </div>
          </div>
        )}

        {isAutoPairModalOpen && (
          <AutoPairModal onClose={() => window.location.reload()}></AutoPairModal>
        )}
      </div>
    </EggTab>
  );
}

function OmnigrowSection({ points, onClick, isLoading, eggs }: OmnigrowSectionProps) {
  const allEggsMaxLevel = eggs.every((egg) => isEggMaxLevel(egg.points));
  const displayParticles = !allEggsMaxLevel && points !== undefined && points > 0;

  let message = null;
  if (allEggsMaxLevel) {
    message = (
      <div className="add-omnigrow-layout">
        <span>All of your eggs have reached max level.</span>
        <div>You can&apos;t add more OmniGrow</div>
      </div>
    );
  } else if (points !== undefined && points > 0) {
    message = (
      <div className="add-omnigrow-layout">
        <span>
          <b>{points}</b> Omnigrow / Egg
        </span>
      </div>
    );
  }

  return (
    <div className="add-omnigrow">
      {message}
      <div className="omnigrow-particles">
        {displayParticles &&
          Array.from({ length: 150 }, (_, i) => <div key={i} className="particle"></div>)}
        <button
          className="btn btn-primary btn-sm add-omnigrow-button"
          onClick={() => {
            if (points !== undefined) {
              onClick(points);
            }
          }}
          disabled={allEggsMaxLevel || isLoading || points === undefined || points <= 0}
        >
          {isLoading ? <Spinner color="white" width={20} height={20} /> : 'ADD OMNIGROW'}
        </button>
      </div>
    </div>
  );
}

type OmnigrowSectionProps = {
  points?: number;
  onClick: (points: number) => void;
  isLoading: boolean;
  eggs: Egg[];
};
